// About Me - Core Page (Using client's exact wording)
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Star } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generatePersonSchema, SchemaScript } from "../components/shared/SEOHelper";
import { TrustBadgesSection } from "../components/shared/TrustBadges";

const AboutMePage = () => {
  usePageMeta({
    title: "About Annelie Lily | Yoga Teacher & Holistic Healer |",
    description: "Annelie Lily is an experienced yoga teacher and holistic healer based in Frome, Somerset. Through yoga, healing and body-based therapies, she supports",
    canonical: "https://annelielily.com/about"
  });

  const personSchema = generatePersonSchema();

  // Testimonials from client's email
  const testimonials = [
    {
      quote: "Joining this yoga group is honestly the best thing I ever did. What started as yoga has become so much more — retreats, women's circles, trips abroad, and friendships that feel like family. We truly support and uplift each other through everything.",
      name: "S",
      location: "Exeter"
    },
    {
      quote: "I joined at a time when I was coming out of a bad relationship and felt like a shell of myself. This community helped me heal, rebuild my confidence, and reconnect with who I really am. I'm now on my own spiritual journey, and I absolutely love it.",
      name: "Community Member",
      location: ""
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={personSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="About Me" currentSlug="about" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              About Annelie Lily
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Yoga Teacher & Holistic Healer
            </h1>
            <p className="text-gold text-lg mb-4">
              Calm Your Mind • Nurture Your Body • Transform Your Life
            </p>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto">
              Based in Frome, Somerset. Supporting people to reconnect with themselves through yoga, 
              healing, women's circles and retreats since 2012.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="about-hero-book-btn"
              >
                Book Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="btn-secondary"
                data-testid="about-hero-contact-btn"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Bio Section */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <div className="prose-content space-y-5 text-neutral-700">
                <p className="text-lg">
                  Annelie Lily is an experienced yoga teacher and holistic healer based in Frome, Somerset. 
                  Through yoga, healing and body-based therapies such as massage, she supports people in 
                  reconnecting with themselves and creating positive, lasting transformation in their lives.
                </p>
                <p>
                  With a background in adult and children's yoga, energy healing and therapeutic bodywork, 
                  Annelie creates a calm, supportive space for those seeking greater balance, wellbeing and clarity.
                </p>
                <p>
                  Her work is rooted in the belief that when we reconnect with our bodies, breath and inner wisdom, 
                  we can begin to heal, grow and live more fully.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_8928e5c6-c128-403f-9029-6531d7ff9ebf/artifacts/rupnbzdd_IMG_2415.jpg"
                  alt="Annelie Lily standing in tree pose with arms raised in mudra inside the warm loft yoga room of her Grade II listed cottage in Frome, with wooden beams, candles and golden window light"
                  className="w-full h-full object-cover"
                  width={600}
                  height={750}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Training & Background */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/dc9ert22_IMG_1638.jpg"
                  alt="Annelie Lily yoga teacher at sunset during a retreat in Spain"
                  className="w-full h-full object-cover"
                  width={600}
                  height={750}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                Training & Background
              </h2>
              <div className="prose-content space-y-5 text-neutral-700">
                <p>
                  Originally trained as a British Wheel of Yoga teacher in 2013, Annelie has explored a wide range 
                  of yoga traditions including Hatha, Iyengar, Ashtanga, Kundalini and Vinyasa Flow. Her teaching 
                  blends traditional BWY principles with an intuitive, nurturing approach that supports everyone.
                </p>
                <p>
                  Annelie has also trained to teach Yin Yoga, Aerial Yoga and Yoga for Children, and is a Reiki Master, Swedish Massage Therapist and Aura Mediator. She has also studied Nutrition.
                </p>
                <p>
                  Annelie originally trained as a solicitor in 1999 and has over 20 years of legal experience.
                </p>
                <p>
                  Yoga has been part of Annelie's life since childhood, when she first practised lion breath at 
                  the age of six. What began as a simple practice grew into a lifelong path of exploration, healing and teaching.
                </p>
                <p>
                  Today, Annelie offers yoga classes, holistic treatments & healing, women's circles and retreats. 
                  Her work brings together movement, breath, energy healing and deep relaxation to support physical 
                  health, emotional wellbeing and spiritual connection.
                </p>
                <p>
                  Annelie Lily was founded in 2012 (originally as Seventh Sense Healing) to provide holistic care across Somerset and Devon.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certified & Accredited - same badges as homepage */}
      <TrustBadgesSection />

      {/* My Approach */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              My Approach
            </h2>
            <div className="prose-content space-y-5 text-neutral-700">
              <p>
                Whether working with individuals or groups, Annelie is passionate about helping people slow down, 
                reconnect with themselves and step into the next chapter of their lives with greater confidence, clarity and ease.
              </p>
              <p className="text-lg font-medium text-neutral-800">
                Annelie's work is transformational.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Nature & Connection */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/n3ebtnc2_IMG_1639.jpg"
                  alt="Annelie Lily sitting peacefully by a river in nature, connecting with the outdoors"
                  className="w-full h-full object-contain bg-cream"
                  width={600}
                  height={600}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                Rooted in Nature
              </h2>
              <div className="prose-content space-y-5 text-neutral-700">
                <p>
                  Nature has always been at the heart of Annelie's practice and teaching. Whether leading 
                  retreats in the Spanish countryside or finding stillness by a Somerset river, she believes 
                  that connecting with the natural world is essential to our wellbeing.
                </p>
                <p>
                  This connection to nature infuses all of her work — from the grounding practices in her 
                  yoga classes to the restorative experiences she creates on retreat.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What I Offer */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ways to Work with Me
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <h3 className="font-heading text-xl text-neutral-800 mb-3">Yoga Classes</h3>
              <p className="text-neutral-600 mb-4">
                Weekly classes to support movement, breath and awareness — a pathway into deeper connection and healing.
              </p>
              <Link to="/yoga-classes" className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                View Classes <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <h3 className="font-heading text-xl text-neutral-800 mb-3">Healing & Treatments</h3>
              <p className="text-neutral-600 mb-4">
                1:1 sessions to support emotional, energetic and physical healing. A space to be held, to release and to gently transform including massage and energy healing techniques.
              </p>
              <Link to="/treatments" className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <h3 className="font-heading text-xl text-neutral-800 mb-3">Retreats</h3>
              <p className="text-neutral-600 mb-4">
                Time away from everyday life to reset, reconnect and go deeper. Combining yoga, healing, nature and rest.
              </p>
              <Link to="/retreats" className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                Explore Retreats <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <h3 className="font-heading text-xl text-neutral-800 mb-3">Women's Circles</h3>
              <p className="text-neutral-600 mb-4">
                A space for women to gather, share and reconnect in a supportive and grounded environment.
              </p>
              <Link to="/womens-circles" className="text-gold font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                Find a Circle <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Deeper Message Section */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gold/5 p-8 rounded-2xl border border-gold/10">
              <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-6 text-center">
                This Work is Not About Fixing Yourself
              </h2>
              <div className="prose-content space-y-4 text-neutral-700 text-center">
                <p>
                  It is about slowing down enough to listen, to feel what is there, and to create space for change to happen naturally.
                </p>
                <p>
                  Many people come to me feeling tired, disconnected or stuck in patterns they don't fully understand.
                </p>
                <p>
                  Through this work, something begins to shift.
                </p>
                <p className="text-lg font-medium text-neutral-800">
                  There is more clarity, more ease, and a deeper connection to yourself and your life.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Work with Me */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Why Work with Me
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                I have been teaching yoga since 2012 and holding space through retreats, circles and healing work since 2015.
              </p>
              <p>
                My approach is grounded, intuitive and supportive.
              </p>
              <p>
                This is not about pushing or forcing change — it is about creating a safe space where transformation can unfold in its own time.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              What People Say
            </span>
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800">
              Community Voices
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/70 p-6 rounded-2xl border border-gold/10"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                  ))}
                </div>
                <p className="text-neutral-700 italic mb-4 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-gold/10 pt-4">
                  <p className="font-medium text-neutral-800">— {testimonial.name}{testimonial.location && `, ${testimonial.location}`}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-neutral-600 italic">
              "This isn't just a yoga group, it's a sisterhood."
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-neutral-800 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gold text-sm tracking-wide mb-4">
              Calm Your Mind • Nurture Your Body • Transform Your Life
            </p>
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              Begin Your Journey
            </h2>
            <p className="text-neutral-300 mb-8 max-w-xl mx-auto">
              To begin your journey with Annelie, you are invited to join a class, book a treatment or healing, 
              attend a circle or explore an upcoming retreat.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/yoga-classes" className="bg-gold/20 hover:bg-gold/30 text-white px-5 py-2.5 rounded-full font-medium transition-colors text-sm">
                Book a Yoga Class
              </Link>
              <Link to="/womens-circles" className="bg-gold/20 hover:bg-gold/30 text-white px-5 py-2.5 rounded-full font-medium transition-colors text-sm">
                Book a Women's Circle
              </Link>
              <Link to="/treatments" className="bg-gold/20 hover:bg-gold/30 text-white px-5 py-2.5 rounded-full font-medium transition-colors text-sm">
                Book a Treatment
              </Link>
              <Link to="/retreats" className="bg-gold/20 hover:bg-gold/30 text-white px-5 py-2.5 rounded-full font-medium transition-colors text-sm">
                Book a Retreat
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutMePage;
